import React, { useMemo, ReactNode, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import get from "lodash/get";
import isEqual from "lodash/isEqual";
import sortBy from "lodash/sortBy";
import classnames from "classnames";

import {
  Button,
  Dialog,
  TextInput,
  CheckboxGroup,
  Loader,
  DraftFileUploader,
} from "fe-shared/src/components";
import useRecycleMaterials from "fe-shared/src/hooks/useRecycleMaterials";
import useProducts from "fe-shared/src/hooks/useProducts";
import useMaterialsByCode from "fe-shared/src/hooks/useMaterialsByCode";
import useNonEligibleMaterials from "fe-shared/src/hooks/useNonEligibleMaterials";
import useValidation from "fe-shared/src/hooks/useValidation";
import { getMergedContent } from "fe-shared/src/helpers/validation";
import {
  BusinessRole,
  Company,
  MaterialCategoryName,
} from "fe-shared/src/types/domain";
import { FileInterface, FileStatus } from "fe-shared/src/types/common";
import {
  CorrectionsFormContent,
  Transaction,
  TransactionType,
} from "../../types";
import DialogHeader from "../DialogHeader";
import useEstimatedWeight from "../../hooks/useEstimatedWeight";
import useAdjustmentReasons from "../../hooks/useAdjustmentReasons";
import { isTransactionWithWeightAndScale } from "../../helpers/transactions";

import { COMMENT_LENGTH_LIMIT, adjustmentReasonOtherCode } from "../../consts";

import {
  getInitialContent,
  errorMessages,
  getValidationConfig,
} from "./modules/validation";
import {
  getStuffGroupProps,
  getCheckboxGroupProps,
  getMaterialsCountConfig,
  getScaleTicketsConfig,
} from "./modules/config";

import styles from "./CorrectionsDialog.module.scss";

export interface Props {
  corrections: CorrectionsFormContent | null;
  transaction: Transaction;
  company?: Company;
  addCorrections?: (values: CorrectionsFormContent) => void;
  applyAction?: (params: unknown) => void;
  isActive: boolean;
  onClose: () => void;
  isAdjustment?: boolean;
  isLoading?: boolean;
  dialogHeader?: ReactNode;
}

const defaultScaleTicket = {
  initialTicketNumber: "",
  correctedTicketNumber: "",
};

const defaultMaterialBusinessRoles = [
  BusinessRole.Hauler,
  BusinessRole.Collector,
  BusinessRole.Processor,
  BusinessRole.OneTimePickup,
];

const CorrectionsDialog = (props: Props) => {
  const {
    transaction,
    corrections = null,
    addCorrections,
    applyAction = () => {},
    isActive,
    onClose,
    isAdjustment = false,
    dialogHeader,
  } = props;

  const { t } = useTranslation();
  const weightsRef = useRef<HTMLDivElement>(null);
  const { data: tires, isLoading: isTiresLoading } = useRecycleMaterials(
    defaultMaterialBusinessRoles
  );
  const { data: products, isLoading: isProductsLoading } = useProducts();
  const {
    data: nonEligibleMaterials,
    isLoading: isNonEligibleLoading,
  } = useNonEligibleMaterials();
  const materialsByCode = useMaterialsByCode(defaultMaterialBusinessRoles);
  const [isWeightErrorVisible, setWeightErrorVisible] = useState(false);

  const isTransactionWithTires =
    transaction.materials[0]?.materialCategoryName ===
    MaterialCategoryName.Tires;
  const availableMaterials = isTransactionWithTires ? tires : products;

  const isWithWeightAndScale = useMemo(
    () => isTransactionWithWeightAndScale(transaction),
    [transaction]
  );

  const ticketNumbers: (number | string)[] = useMemo(() => {
    const isScaleTickets = !!transaction.scaleTickets.length;

    return isScaleTickets
      ? transaction.scaleTickets.map((item) => item.ticketNumber)
      : [""];
  }, [transaction]);

  const {
    data: allReasons,
    isLoading: isLoadingAdjustmentReasons,
  } = useAdjustmentReasons(isAdjustment);

  const mappedAttachments: FileInterface[] = useMemo(
    () =>
      isAdjustment && transaction.attachments
        ? transaction.attachments.map((item) => ({
            ...item,
            path: item.documentPath,
            id: item.documentPath,
            status: FileStatus.Loaded,
          }))
        : [],
    [isAdjustment, transaction.attachments]
  );

  const initialContent = useMemo(
    () =>
      getInitialContent(
        isAdjustment,
        mappedAttachments,
        isWithWeightAndScale,
        transaction.comment || ""
      ),
    [isAdjustment, mappedAttachments, isWithWeightAndScale, transaction.comment]
  );

  const mergedContent = useMemo(() => {
    const {
      materials,
      materialAttributes,
      grossWeight,
      tareWeight,
      adjustmentReasons,
      adjustmentOtherReasonComment,
    } = transaction;

    const mappedMaterials = materials.map(({ materialCode, units }) => ({
      materialCode,
      units,
    }));

    const scaleTickets = ticketNumbers.map((initialTicketNumber, index) => ({
      ticketId: transaction.scaleTickets[index]?.ticketId,
      initialTicketNumber,
      correctedTicketNumber: initialTicketNumber,
    }));

    const defaultCorrectionContent = {
      materials: mappedMaterials,
      materialAttributes,
      grossWeight,
      tareWeight,
      scaleTickets,
      adjustmentReasons: adjustmentReasons || [],
      adjustmentOtherReasonComment: adjustmentOtherReasonComment || "",
    };

    const correctionsFormContent =
      corrections && !isAdjustment
        ? {
            ...defaultCorrectionContent,
            ...corrections,
          }
        : defaultCorrectionContent;

    return getMergedContent(initialContent, correctionsFormContent);
  }, [transaction, ticketNumbers, corrections, isAdjustment, initialContent]);

  const validationConfig = useMemo(
    () =>
      getValidationConfig(
        isAdjustment,
        isWithWeightAndScale,
        isTransactionWithTires
      ),
    [isAdjustment, isWithWeightAndScale, isTransactionWithTires]
  );

  const {
    errors,
    values,
    isValid,
    validateAll,
    updateValue,
    validateValue,
    addArrayValue,
    removeArrayValue,
  } = useValidation(validationConfig, mergedContent, errorMessages);

  const isCommentTooLong = values.comment.length > COMMENT_LENGTH_LIMIT;
  const characterLimitText = `${values.comment.length.toLocaleString()}/${COMMENT_LENGTH_LIMIT.toLocaleString()}`;
  const isAdjustmentTransaction =
    transaction.transactionType === TransactionType.Adjustment;

  const materialsCheckboxGroupProps = useMemo(
    () => getCheckboxGroupProps(availableMaterials),
    [availableMaterials]
  );

  const materialsCountConfig = useMemo(
    () =>
      getMaterialsCountConfig(
        materialsByCode,
        values.materials,
        isTransactionWithTires
      ),
    [materialsByCode, values, isTransactionWithTires]
  );

  const tireTypeCodes = useMemo(
    () => values.materials.map((tire) => tire.materialCode),
    [values]
  );

  const scaleTicketsConfig = useMemo(
    () => getScaleTicketsConfig(ticketNumbers, t),
    [t, ticketNumbers]
  );

  const isWeightMismatch = useMemo(() => {
    if (!isTransactionWithTires && isWithWeightAndScale) {
      const grossWeight = values?.grossWeight || 0;
      const tareWeight = values?.tareWeight || 0;
      const materialsWeight = values.materials.reduce(
        (acc, item) => acc + item.units.unitQuantity,
        0
      );
      const netWeight = grossWeight - tareWeight;

      return Math.abs(materialsWeight - netWeight) > 0;
    }

    return false;
  }, [isTransactionWithTires, isWithWeightAndScale, values]);

  const onTiresChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    checkedValues: unknown[]
  ) => {
    if (checkedValues.length > tireTypeCodes.length) {
      const materialCode = checkedValues.find(
        (c) => !tireTypeCodes.includes(c as string)
      );

      const oldValue = transaction.materials.find(
        (i) => i.materialCode === materialCode
      )?.units.unitQuantity;

      const {
        units: { unit },
      } = transaction.materials[0];

      addArrayValue("materials", {
        materialCode,
        units: {
          unit,
          unitQuantity: oldValue || 0,
        },
      });
    } else {
      const index = tireTypeCodes.findIndex(
        (tireCode) => !checkedValues.includes(tireCode)
      );

      removeArrayValue("materials", index);
    }
  };

  const onSubmit = () => {
    setWeightErrorVisible(isWeightMismatch);

    if (isWeightMismatch) {
      weightsRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }

    if (isValid) {
      if (isAdjustment) {
        applyAction({
          transactionId: transaction.transactionId,
          content: values,
        });
      } else {
        const currentAction = addCorrections || applyAction;

        currentAction(values);
      }
    } else {
      validateAll();
    }
  };

  const isContentEqual = useMemo(() => {
    const {
      materialAttributes: initialFormAttributes,
      materials: initialFormMaterials,
      ...initialFormContent
    } = mergedContent;

    const {
      materialAttributes: formAttributes,
      materials: formMaterials,
      ...formContent
    } = values;

    const isFormContentEqual = isEqual(initialFormContent, formContent);
    const isAttributesEqual = isEqual(
      sortBy(initialFormAttributes),
      sortBy(formAttributes)
    );
    const isMaterialsEqual = isEqual(
      sortBy(initialFormMaterials, ["materialCode"]),
      sortBy(formMaterials, ["materialCode"])
    );

    return isFormContentEqual && isAttributesEqual && isMaterialsEqual;
  }, [mergedContent, values]);

  const isWeightError = isWeightErrorVisible && isWeightMismatch;

  const isSaveDisabled =
    !!Object.keys(errors).length ||
    isCommentTooLong ||
    isContentEqual ||
    isWeightError;

  const updateNumberValue = (key: string, value: string | number) => {
    const valueNumber = Number(value);
    const isCorrectValue = !Number.isNaN(valueNumber);
    const savedValue = isCorrectValue ? valueNumber : value;

    updateValue(key, savedValue, true);
  };

  const updateScaleTicketValue = (
    valueKey: string,
    value: string,
    index: number
  ) => {
    const scaleTicketValue = values.scaleTickets![index];
    const isSecondScaleTicket = index === 1;
    const isSecondScaleTicketExists = isSecondScaleTicket && !!scaleTicketValue;
    const isInitialTicketNumber = !!values.scaleTickets![index]
      ?.initialTicketNumber;

    if (!scaleTicketValue) {
      addArrayValue("scaleTickets", defaultScaleTicket);
    }

    updateValue(valueKey, value, true);

    if (!value && isSecondScaleTicketExists && !isInitialTicketNumber) {
      removeArrayValue("scaleTickets", index);
    }
  };

  const actions = [
    <Button
      key="1"
      id="corrections-dialog-save-btn"
      disabled={isSaveDisabled}
      variant="filled"
      onClick={onSubmit}
    >
      {t(`correction.dialog.save`)}
    </Button>,
    <Button
      key="2"
      onClick={onClose}
      variant="outlined"
      id="corrections-dialog-cancel-btn"
    >
      {t("cancel")}
    </Button>,
  ];

  const title = dialogHeader || (
    <DialogHeader
      type={transaction.transactionType}
      title={t("correction.dialog.title")}
      translationRoot="transactions"
      number={transaction.transactionNumber}
    />
  );

  const estimatedWeight = useEstimatedWeight(
    values.materials,
    defaultMaterialBusinessRoles
  );

  const isLoading =
    props.isLoading ||
    isTiresLoading ||
    isProductsLoading ||
    isNonEligibleLoading ||
    isLoadingAdjustmentReasons;
  const isOtherAdjustmentReasons = values.adjustmentReasons?.includes(
    adjustmentReasonOtherCode
  );

  const getOriginalMaterialQuantity = (code: string) => {
    const originalMaterials =
      transaction.originalValues?.materials || transaction.materials;

    return originalMaterials.find((material) => material.materialCode === code)
      ?.units.unitQuantity;
  };

  const action = useMemo(
    () =>
      isAdjustmentTransaction
        ? transaction.linkedTransactions
            .map((item) =>
              t(`correction.dialog.form.action.${item.transactionType}`)
            )
            .join("/")
        : t(`correction.dialog.form.action.${transaction.transactionType}`),
    [
      isAdjustmentTransaction,
      t,
      transaction.linkedTransactions,
      transaction.transactionType,
    ]
  );

  return (
    <Dialog
      modal
      width={608}
      title={title}
      isActive={isActive}
      onClose={onClose}
      actions={actions}
    >
      {isLoading && <Loader isBlocking />}
      {isAdjustment && (
        <>
          <span className={styles.formSectionTitle}>
            {t("correction.dialog.form.adjustmentReasonsTitle")}
            <span className={styles.titleRequired}>*</span>
          </span>

          <CheckboxGroup
            id="adjustmentReasons"
            value={values.adjustmentReasons!}
            onChange={(
              e: React.ChangeEvent<HTMLInputElement>,
              value: unknown[]
            ) => updateValue("adjustmentReasons", value, true)}
            isError={!!errors.adjustmentReasons}
            errorText={errors.adjustmentReasons}
            name="correction-adjustment-reasons-checkbox-group"
            variants={allReasons?.adjustmentReasonsVariants || []}
            wrapperClassName={styles.checkboxWrapper}
          />

          {isOtherAdjustmentReasons && (
            <TextInput
              id="adjustmentOtherReasonComment"
              title={t("correction.dialog.form.adjustmentOtherCommentTitle")}
              errorText={errors.adjustmentOtherReasonComment}
              isError={!!errors.adjustmentOtherReasonComment}
              value={values.adjustmentOtherReasonComment}
              width="100%"
              wrapperClassName={styles.weightCountInput}
              onBlur={() => validateValue("adjustmentOtherReasonComment")}
              onChange={(e, value) => {
                updateValue("adjustmentOtherReasonComment", value, true);
              }}
            />
          )}
        </>
      )}

      <span className={styles.formSectionTitle}>
        {t(
          `correction.dialog.form.${
            isTransactionWithTires ? "tires" : "products"
          }Title`,
          { action }
        )}
        <span className={styles.titleRequired}>*</span>
      </span>
      <CheckboxGroup
        id="materials"
        value={tireTypeCodes}
        onChange={onTiresChange}
        isError={!!errors.materials}
        errorText={errors.materials}
        {...materialsCheckboxGroupProps}
        wrapperClassName={styles.checkboxWrapper}
      />
      {!!materialsCountConfig.length && (
        <>
          <span className={styles.formSectionTitle}>
            {t(
              `correction.dialog.form.${
                isTransactionWithTires ? "tires" : "products"
              }CountTitle`
            )}
            <span className={styles.titleRequired}>*</span>
          </span>
          {materialsCountConfig.map(({ valueKey, componentProps }, index) => {
            const { title: tireTitle, ...otherProps } = componentProps;

            return (
              <div key={valueKey} className={styles.tiresCount}>
                <div className={styles.tiresCountTitle}>{tireTitle}</div>
                <TextInput
                  id={valueKey}
                  errorText={errors[valueKey]}
                  isError={!!errors[valueKey]}
                  value={get(values, valueKey) || ""}
                  {...otherProps}
                  onBlur={() => validateValue(valueKey)}
                  onChange={(e, value) => updateNumberValue(valueKey, value)}
                />
                {isAdjustment && (
                  <span className={styles.tiresCountOriginal}>
                    {getOriginalMaterialQuantity(
                      values.materials[index].materialCode
                    )}
                  </span>
                )}
              </div>
            );
          })}
        </>
      )}
      {isTransactionWithTires && (
        <>
          <span className={styles.formSectionSubtitle}>
            {t("correction.dialog.form.otherStuff")}
          </span>
          <CheckboxGroup
            inline
            id="materialAttributes"
            value={values.materialAttributes}
            wrapperClassName={styles.checkboxWrapper}
            onChange={(e, value) => {
              updateValue("materialAttributes", value, true);
            }}
            isError={!!errors.materialAttributes}
            errorText={errors.materialAttributes}
            {...getStuffGroupProps(nonEligibleMaterials)}
          />
        </>
      )}
      {isWithWeightAndScale && (
        <>
          <span className={styles.formSectionTitle}>
            {t("correction.dialog.form.scaleTicketTitle")}
          </span>
          <div className={styles.inlineInputs}>
            {scaleTicketsConfig.map(
              ({ valueKey, componentProps }, scaleTicketIndex) => {
                const errorText =
                  scaleTicketIndex === 0
                    ? errors.scaleTickets || errors[valueKey]
                    : errors[valueKey];

                return (
                  <TextInput
                    id={valueKey}
                    key={valueKey}
                    errorText={errorText}
                    isError={!!errorText}
                    value={get(values, valueKey)}
                    {...componentProps}
                    onBlur={() => validateValue(valueKey)}
                    onChange={(e, value) => {
                      updateScaleTicketValue(valueKey, value, scaleTicketIndex);
                    }}
                  />
                );
              }
            )}
          </div>
          <>
            <div className={styles.weightSection} ref={weightsRef}>
              <span>{t("transactions.details.weights.netWeight")}</span>
              <span
                className={classnames(isWeightError && styles.weightCountError)}
              >
                {`${(
                  values.grossWeight! - values.tareWeight!
                )?.toLocaleString()} kg`}
              </span>
            </div>
            {isWeightError && (
              <span className={styles.weightMismatchError}>
                {t("validation.weightMismatch")}
              </span>
            )}
          </>
          {isTransactionWithTires && (
            <>
              <div className={styles.estimatedWeightSection}>
                <span>{t("transactions.details.weights.estimatedWeight")}</span>
                <span>{`${estimatedWeight?.toLocaleString()} kg`}</span>
              </div>
            </>
          )}
          <div className={styles.weightCount}>
            <label htmlFor="grossWeight" className={styles.weightCountTitle}>
              {t("transactions.details.weights.grossWeight")}
              <span className={styles.titleRequired}>*</span>
            </label>
            <TextInput
              id="grossWeight"
              mask="99999999999999"
              errorText={errors.grossWeight}
              isError={!!errors.grossWeight}
              value={values.grossWeight}
              width={102}
              wrapperClassName={styles.weightCountInput}
              onBlur={() => validateValue("grossWeight")}
              onChange={(e, value) => updateNumberValue("grossWeight", value)}
            />
            {isAdjustment && (
              <span className={styles.weightCountOriginal}>
                {transaction.originalValues?.grossWeight ||
                  transaction.grossWeight}
              </span>
            )}
          </div>
          <div className={styles.weightCount}>
            <label htmlFor="tareWeight" className={styles.weightCountTitle}>
              {t("transactions.details.weights.tareWeight")}
              <span className={styles.titleRequired}>*</span>
            </label>
            <TextInput
              id="tareWeight"
              mask="99999999999999"
              errorText={errors.tareWeight}
              isError={!!errors.tareWeight}
              value={values.tareWeight}
              width={102}
              wrapperClassName={styles.weightCountInput}
              onBlur={() => validateValue("tareWeight")}
              onChange={(e, value) => updateNumberValue("tareWeight", value)}
            />
            {isAdjustment && (
              <span className={styles.weightCountOriginal}>
                {transaction.originalValues?.tareWeight ||
                  transaction.tareWeight}
              </span>
            )}
          </div>
        </>
      )}
      <TextInput
        isMultiline
        id="comment"
        title={t("correction.dialog.inputs.comment")}
        errorText={t("validation.commentMaxLength", { characterLimitText })}
        isError={isCommentTooLong}
        value={values.comment}
        onBlur={() => validateValue("comment")}
        onChange={(e, value) => updateValue("comment", value, true)}
      />
      <span className={styles.formSectionTitle}>
        {t("correction.dialog.form.documentsTitle")}
      </span>
      <DraftFileUploader
        onChange={(files) => updateValue("attachments", files, true)}
        path="transactions/attachment"
        files={values.attachments}
      />
    </Dialog>
  );
};

export default CorrectionsDialog;
